import React, { useRef, useState } from 'react';

export const AnimatedLoginBackground = ({ children }: { children: React.ReactNode }) => {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const [isHovering, setIsHovering] = useState(false);

    const handleMouseMove = (e: React.MouseEvent) => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setMousePosition({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
        }
    };

    return (
        <div
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            className="relative h-screen w-full overflow-hidden bg-slate-950 flex items-center justify-center p-0 m-0"
        >
            {/* Base gradient setting the mood */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/80 via-slate-950 to-violet-950/80" />

            {/* Base faint grid */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: `
                        linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
                    `,
                    backgroundSize: '4rem 4rem'
                }}
            />

            {/* Highlighted grid on hover (Tech glow passing through lines) */}
            <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-500 ease-out"
                style={{
                    opacity: isHovering ? 1 : 0,
                    backgroundImage: `
                        linear-gradient(to right, rgba(129, 140, 248, 0.3) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(129, 140, 248, 0.3) 1px, transparent 1px)
                    `,
                    backgroundSize: '4rem 4rem',
                    maskImage: `radial-gradient(circle 300px at ${mousePosition.x}px ${mousePosition.y}px, black 0%, transparent 100%)`,
                    WebkitMaskImage: `radial-gradient(circle 300px at ${mousePosition.x}px ${mousePosition.y}px, black 0%, transparent 100%)`
                }}
            />

            {/* Glowing cursor effect (Soft ambient glow) */}
            <div
                className="pointer-events-none absolute inset-0 transition-opacity duration-500 ease-out z-0"
                style={{
                    opacity: isHovering ? 1 : 0,
                    background: `radial-gradient(circle 500px at ${mousePosition.x}px ${mousePosition.y}px, rgba(99, 102, 241, 0.12), transparent 70%)`,
                }}
            />

            {/* Content */}
            <div className="relative z-10 w-full max-w-5xl flex items-center justify-center h-full">
                {children}
            </div>
        </div>
    );
};
